// src/pages/product/PartnerProducts.tsx
//
// PartnerProducts (Partner-only products view)
// - Uses the shared SearchPanel via PartnerProductsSearchPanel.
// - Keeps URL query params in sync with the *applied* search state (shareable deep links).
// - Uses "draft vs applied" search state to avoid filtering while typing.
// - Auto-search is opt-in per page (do NOT implement debounce here; delegate to SearchPanel).
//
// Table policy (shared UX):
// - Replace legacy list controls with StandardTableToolbar + StandardPagination.
// - Use StandardTableFrame for loading/empty states.
// - Navigation rule: do NOT navigate on row click.
//   Navigate when clicking the Product Code cell.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { utcToZonedTime } from 'date-fns-tz';

import { exportPartnerProductsExcel, type GetPartnerProductsParams, ProductSaleType } from '@apis/product';

import { usePartnerProductList } from '@queries/products/usePartnerProductList';
import { usePartnerProductCount } from '@queries/products/usePartnerProductCount';
import { useCountryList } from '@queries/countries/useCountryList';

import { filterSearchParams } from '@utils/search';
import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { downloadBlob } from '@utils/file';
import { PRODUCT_SALE_TYPE_LABELS } from '@utils/productLabels';

import type { SetSearchValueFn } from '@components/common/search';
import PartnerProductsSearchPanel from '@components/product/PartnerProductsSearchPanel';
import { ProductSaleTypeDisplay } from '@components/product/ProductSaleTypeDisplay';
import {
  ProductDataCapacityDisplay,
  ProductDataTypeDisplay,
  ProductFupDisplay,
  ProductTypeDisplay,
  ProductValidityDisplay,
  ProductVoiceDisplay,
  RequiredInfoIcons,
} from '@components/product/PartnerProductSpecDisplays';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { StandardTableToolbar, StandardTableFrame, StandardPagination, cellSx } from '@components/common/table';

type ProductSearchForm = GetPartnerProductsParams;

type Option<T = any> = {
  label: string;
  value: T;
};

const PAGE_SIZE = 50;

/**
 * Page-level auto-search policy:
 * - Global default autoSearch is OFF (SearchPanel constants).
 * - Enable autoSearch only for this page if desired.
 */
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const sortByLabel = <T,>(a: Option<T>, b: Option<T>) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const isValidProductSaleType = (v: string | null): v is ProductSaleType => {
  if (!v) return false;
  return (Object.values(ProductSaleType) as string[]).includes(v);
};

/**
 * Normalize search form before calling APIs / building query string.
 * - Trim string values
 * - Drop invalid/empty values
 */
const cleanSearchForm = (form: ProductSearchForm): ProductSearchForm => {
  const code = normalizeText(form.productCode);
  const name = normalizeText(form.productName);

  return {
    productCode: code,
    productName: name,
    countryId: typeof form.countryId === 'number' ? form.countryId : undefined,
    productSaleType: form.productSaleType || undefined,
  };
};

const readSearchFromUrl = (sp: URLSearchParams): ProductSearchForm => {
  const saleTypeRaw = sp.get('productSaleType');

  const countryIdRaw = sp.get('countryId');
  const countryId = countryIdRaw ? Number(countryIdRaw) : undefined;

  return {
    productCode: sp.get('productCode') || '',
    productName: sp.get('productName') || '',
    countryId: Number.isFinite(countryId as number) ? countryId : undefined,
    productSaleType: isValidProductSaleType(saleTypeRaw) ? saleTypeRaw : undefined,
  };
};

const buildUrlParams = (form: ProductSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const params: Record<string, string> = {};
  if (cleaned.productCode) params.productCode = cleaned.productCode;
  if (cleaned.productName) params.productName = cleaned.productName;
  if (typeof cleaned.countryId === 'number') params.countryId = String(cleaned.countryId);
  if (cleaned.productSaleType) params.productSaleType = String(cleaned.productSaleType);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

export const PartnerProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const systemAlert = useSystemAlert();

  /* ------------------------------------------------------------------ */
  /* Search state (draft vs applied) + URL sync                          */
  /* ------------------------------------------------------------------ */

  const [appliedSearch, setAppliedSearch] = useState<ProductSearchForm>(() => readSearchFromUrl(searchParams));
  const [draftSearch, setDraftSearch] = useState<ProductSearchForm>(() => ({ ...appliedSearch }));

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  // Filter panel is collapsed by default.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Keep state in sync when URL changes (deep link / back-forward / manual URL edit).
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setAppliedSearch(nextApplied);

    setDraftSearch((prev) => {
      const a = cleanSearchForm(prev);
      const b = cleanSearchForm(nextApplied);

      const same =
        (a.productCode ?? '') === (b.productCode ?? '') &&
        (a.productName ?? '') === (b.productName ?? '') &&
        (a.countryId ?? null) === (b.countryId ?? null) &&
        (a.productSaleType ?? null) === (b.productSaleType ?? null);

      return same ? prev : nextApplied;
    });

    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const syncUrlFromApplied = useCallback(
    (nextApplied: ProductSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  const handleSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const handleReset = useCallback(() => {
    const cleared: ProductSearchForm = {
      productCode: '',
      productName: '',
      countryId: undefined,
      productSaleType: undefined,
    };

    setDraftSearch(cleared);

    const nextApplied = cleanSearchForm(cleared);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /**
   * SearchPanel input/chip setter (null = clear).
   * - Text fields use "" so the draft stays controlled.
   * - Select fields use undefined so cleanSearchForm can drop them.
   */
  const setValue: SetSearchValueFn<ProductSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      if (key === 'productCode' || key === 'productName') {
        next[key] = (value ?? '') as string;
      } else {
        next[key] = (value ?? undefined) as unknown;
      }

      return next as ProductSearchForm;
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Queries (driven by appliedSearch + page)                            */
  /* ------------------------------------------------------------------ */

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const { data, isLoading } = usePartnerProductList({ page, searchParam: cleanedApplied });
  const { data: totalCount, isLoading: isLoadingCount } = usePartnerProductCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows = data?.data ?? [];

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  // Clamp current page if total count changes (filters applied, etc.).
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  /* ------------------------------------------------------------------ */
  /* Options (country / sale type)                                       */
  /* ------------------------------------------------------------------ */

  const { data: countriesData } = useCountryList();

  const countryOptions: Option<number>[] = useMemo(() => {
    const raw = countriesData?.data ?? [];
    return raw.map((c) => ({ label: c.englishName, value: c.countryId })).sort(sortByLabel);
  }, [countriesData]);

  const saleTypeOptions: Option<ProductSaleType>[] = useMemo(() => {
    const values = Object.values(ProductSaleType) as ProductSaleType[];
    return values.map((t) => ({ value: t, label: PRODUCT_SALE_TYPE_LABELS[t] ?? t })).sort(sortByLabel);
  }, []);

  const selectedCountry = useMemo(() => {
    if (!draftSearch.countryId) return null;
    return countryOptions.find((o) => o.value === draftSearch.countryId) ?? null;
  }, [countryOptions, draftSearch.countryId]);

  const selectedSaleType = useMemo(() => {
    if (!draftSearch.productSaleType) return null;
    return saleTypeOptions.find((o) => o.value === draftSearch.productSaleType) ?? null;
  }, [saleTypeOptions, draftSearch.productSaleType]);

  /* ------------------------------------------------------------------ */
  /* Export (Excel) based on appliedSearch                               */
  /* ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);

      const res = await exportPartnerProductsExcel(cleanedApplied);

      const nowUtc = new Date();
      const kstDate = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);
      const fileName = `partner-products-${kstDate.toISOString()}.xlsx`;

      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied, isExporting]);

  /* ------------------------------------------------------------------ */
  /* Paging (URL is the source of truth for applied state)               */
  /* ------------------------------------------------------------------ */

  const handleChangePage = useCallback(
    (next: number) => {
      const clamped = clamp(next, 1, totalPages);
      setPage(clamped);
      syncUrlFromApplied(appliedSearch, clamped);
    },
    [appliedSearch, syncUrlFromApplied, totalPages],
  );

  /* ------------------------------------------------------------------ */
  /* Navigation helpers (Product Code opens detail)                      */
  /* ------------------------------------------------------------------ */

  const handleOpenDetail = useCallback(
    (productId: number) => {
      if (!Number.isFinite(productId) || productId <= 0) return;
      navigate(`/my-products/${productId}`);
    },
    [navigate],
  );

  const handleCopyProductCode = useCallback(
    async (productCode: unknown) => {
      const text = String(productCode ?? '').trim();
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        systemAlert.success('Product code copied.', { durationMs: 1600 });
      } catch {
        systemAlert.error('Copy failed.');
      }
    },
    [systemAlert],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                               */
  /* ------------------------------------------------------------------ */

  // (# + Product Code + Product Name + Product Type + Order Type + Data Type + Data + FUP + Validity + Voice + Required)
  const columnsCount = 11;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PartnerProductsSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draftSearch={draftSearch}
        setValue={setValue}
        countryOptions={countryOptions}
        saleTypeOptions={saleTypeOptions}
        selectedCountry={selectedCountry}
        selectedSaleType={selectedSaleType}
        onSearch={handleSearch}
        onReset={handleReset}
        onExport={handleExport}
        exportLoading={isExporting}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<any>
          containerSx={{ borderRadius: 2 }}
          minWidth={1515}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell width={210}>Product Code</TableCell>
              <TableCell sx={{ minWidth: 260 }}>Product Name</TableCell>
              <TableCell width={125} sx={cellSx.nowrap}>
                Product Type
              </TableCell>
              <TableCell width={125} sx={cellSx.nowrap}>
                Order Type
              </TableCell>
              <TableCell width={130} sx={cellSx.nowrap}>
                Data Type
              </TableCell>
              <TableCell width={100} sx={cellSx.nowrap}>
                Data
              </TableCell>
              <TableCell width={70} align="center" sx={cellSx.nowrap}>
                FUP
              </TableCell>
              <TableCell width={100} sx={cellSx.nowrap}>
                Validity
              </TableCell>
              <TableCell width={105} sx={cellSx.nowrap}>
                Voice
              </TableCell>
              <TableCell width={115} align="center" sx={cellSx.nowrap}>
                Required
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.productId)}
          renderRow={(row, index) => {
            const productId = Number(row.productId);
            const canNavigate = Number.isFinite(productId) && productId > 0;
            const productCode = String(row.productCode ?? '').trim();
            const canCopyProductCode = Boolean(productCode);
            const spec = row.specification ?? {};

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
                    {canNavigate ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpenDetail(productId)}
                        sx={{
                          minWidth: 0,
                          px: 0,
                          fontWeight: 800,
                          textTransform: 'none',
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                        }}
                      >
                        {row.productCode || '-'}
                      </Button>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {row.productCode || '-'}
                      </Typography>
                    )}
                    {canCopyProductCode && (
                      <Tooltip title="Copy product code" arrow>
                        <IconButton
                          size="small"
                          aria-label="Copy product code"
                          onClick={() => handleCopyProductCode(productCode)}
                          sx={{
                            width: 24,
                            height: 24,
                            color: 'text.secondary',
                            opacity: 0.9,
                            flexShrink: 0,
                            '&:hover': {
                              opacity: 1,
                              color: 'primary.main',
                            },
                          }}
                        >
                          <ContentCopy sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ minWidth: 260 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {row.productName || '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductTypeDisplay productType={row.productType} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductSaleTypeDisplay saleType={row.productSaleType} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductDataTypeDisplay specification={spec} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductDataCapacityDisplay specification={spec} />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
                    <ProductFupDisplay specification={spec} />
                  </Box>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductValidityDisplay dayCount={spec.dayCount} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <ProductVoiceDisplay specification={spec} />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <RequiredInfoIcons
                    requiredFields={row.requiredData?.requiredFields}
                    saleType={row.productSaleType}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default PartnerProducts;
